import { inject, Injectable, signal } from '@angular/core';

import { UpdateEdgeTypeResponse } from '@reach/interfaces/edge-types/edge-types.responses';
import { EdgeTypeInterface } from '@reach/interfaces/edge-types/edge-types.types';
import { CreateEdgeRequestInterface } from '@reach/interfaces/edges/edges.requests';

import { EdgesApiService } from '~app-client/api/services';

import { EdgeTypeFormValue } from '../components/relationship-modal/relationship-modal.component';

@Injectable({
	providedIn: 'root',
})
export class RelationshipEdgeTypesService {
	public readonly $edgeTypes = signal<EdgeTypeInterface[]>([]);

	private readonly edgesApi = inject(EdgesApiService);

	public async getEdgeTypes(): Promise<void> {
		try {
			const response = await this.edgesApi.getEdgeTypes();
			this.$edgeTypes.set(response);
		} catch (error) {
			console.error(error);
		}
	}

	public upsertEdgeType(edgeType: EdgeTypeInterface): void {
		this.$edgeTypes.update((list) => {
			const index = list.findIndex((item) => item.id === edgeType.id);
			if (index === -1) {
				return [...list, edgeType];
			}
			const next = [...list];
			next[index] = edgeType;
			return next;
		});
	}

	public async createEdgeType(value: EdgeTypeFormValue): Promise<EdgeTypeInterface> {
		try {
			const created = await this.edgesApi.createEdgeType({
				name: value.name,
				description: value.description,
				color: value.color,
				weight: value.weight,
			});
			this.upsertEdgeType(created);
			return created;
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	public async updateEdgeType(
		id: EdgeTypeInterface['id'],
		value: EdgeTypeFormValue
	): Promise<UpdateEdgeTypeResponse> {
		try {
			const updated = await this.edgesApi.updateEdgeType(id, {
				name: value.name,
				description: value.description,
				color: value.color,
				weight: value.weight,
			});
			this.upsertEdgeType(updated);
			return updated;
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	public async deleteEdgeType(id: EdgeTypeInterface['id']): Promise<void> {
		try {
			await this.edgesApi.deleteEdgeType(id);
		} catch (error) {
			console.error(error);
		}
	}

	public async createEdges(edges: CreateEdgeRequestInterface[]): Promise<void> {
		try {
			await this.edgesApi.createMany(edges);
		} catch (error) {
			console.error(error);
		}
	}
}
